import React, { useContext, useState, lazy, Suspense } from 'react';
import proptypes from 'prop-types';
const {
  bool: boolType,
  shape: shapeType,
  number: numberType,
  string: stringType,
  arrayOf: arrayOfType,
  oneOfType: oneOfTypeType
} = proptypes;
import { RatingMeter, Typography } from '@one-thd/sui-atomic-components';
import {
  QueryProvider,
  params,
  string,
  number,
  bool,
  shape,
  extend,
  arrayOf
} from '@thd-nucleus/data-sources';
import { Image } from '@thd-olt-component-react/image';
import { useStoreId } from '@thd-nucleus/experience-context';
import { KeyProductFeatures } from '@thd-olt-component-react/key-product-features';
import { Price } from '@thd-olt-component-react/price';
import { ProductHeader } from '@thd-olt-component-react/product-details';
import { ProductImage, ProductPod, ProductRating } from '@thd-olt-component-react/product-pod';
import { SwapContext } from '../../SwapContext';
import { SelectionButton } from '../selection-button/SelectionButton';
import { getDimensions } from './dimensions';

const QuickViewLazy = lazy(() => import(
  /* webpackChunkName: "quick-view" */
  '@thd-olt-component-react/quick-view'
).then((mod) => {
  const { QuickView } = mod;
  return {
    default: (props) => {
      return (
        <QueryProvider
          dataSource="catalog"
          cacheKey="swap-item-quick-item"
        >
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <QuickView {...props} />
        </QueryProvider>
      );
    }
  };
}));

// Clicks on the pod's own links and buttons (product name, image, rating, price links) are handled
// by those elements, so the pod-level click only covers the rest of the pod
const INTERACTIVE_SELECTOR = 'a, button, input, select, textarea, [role="button"], [role="link"]';

export const ProductPodWrapper = ({
  hideKPF,
  showDimensions,
  dimensionsDisplay,
  hideSelectionButton,
  openQuickViewOnPodClick,
  product,
  swapItemPosition,
  openInQuickViewDescription,
  openDetailsInNewTab,
  hideQuickViewMask
}) => {
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
  const [mountQuickView, setMountQuickView] = useState(false);
  const [isRatingsOpen, setIsRatingsOpen] = useState(false);
  const storeId = useStoreId();
  const { onClose } = useContext(SwapContext);

  const handleOnOpenQuickView = (event) => {
    event.preventDefault();
    setIsQuickViewOpen(true);
  };

  const handlePodClick = (event) => {
    if (event.target?.closest?.(INTERACTIVE_SELECTOR)) return;
    setIsQuickViewOpen(true);
  };

  const { averageRating, totalReviews } = product?.reviews?.ratingsReviews || {};
  const dimensions = showDimensions ? getDimensions(product?.specificationGroup, dimensionsDisplay?.specs) : null;

  return (
    <div
      onMouseEnter={(event) => {
        event.preventDefault();
        if (!mountQuickView) setMountQuickView(true);
      }}
      data-testid="swap-item-product-pod"
    >
      <ProductPod
        itemId={product.itemId}
        storeId={storeId}
        analyticsData={{ parent: 'swap-item', position: swapItemPosition }}
        render={(pod) => (
          <>
            {/* The pod-level click is a pointer convenience; the product name and image stay the
                keyboard-accessible ways to open QuickView */}
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <div
              className={`sui-row sui-flex${openQuickViewOnPodClick ? ' sui-cursor-pointer' : ''}`}
              onClick={openQuickViewOnPodClick ? handlePodClick : undefined}
              data-testid="swap-item-product-pod__content"
            >
              <div className="sui-col">
                <div className="sui-flex sui-w-20 sui-h-20 sui-pr-2">
                  <ProductImage
                    itemId={pod.itemId}
                    onClick={handleOnOpenQuickView}
                    skeletonSize={30}
                  />
                </div>
              </div>
              <div className="sui-col sui-w-full sui-px-2 sui-mr-2">
                <ProductHeader
                  brand="above"
                  itemId={pod.itemId}
                  brandTitleMaxLine={4}
                  brandFontSize="base"
                  productNameFontSize="base"
                  brandLineHeight="normal"
                  productNameLineHeight="normal"
                  disableShopThisCollection
                  onClick={handleOnOpenQuickView}
                  data-testid="product-header"
                />
                <div className="sui-flex sui-items-center sui-h-6 sui-mb-3">
                  <RatingMeter
                    className="hover:sui-cursor-pointer sui-p-0"
                    onClick={() => {
                      setIsRatingsOpen(true);
                      setIsQuickViewOpen(true);
                    }}
                    value={parseFloat(averageRating)}
                    label={<>&nbsp;{`(${totalReviews})`}</>}
                    edge="start"
                    RatingProps={{
                      color: 'brand'
                    }}
                  />
                </div>
                <div className="sui-mb-3">
                  <Price
                    itemId={pod.itemId}
                    large={false}
                    storeId={storeId}
                    displayEachUom={false}
                  />
                </div>
                {dimensions && (
                  <div className="sui-mb-3">
                    <Typography
                      variant={dimensionsDisplay?.variant || 'body-xs'}
                      color={dimensionsDisplay?.variant ? undefined : 'subtle'}
                      data-testid="product-pod-wrapper__dimensions"
                    >
                      {dimensions}
                    </Typography>
                  </div>
                )}
                {!hideKPF && (
                  <div
                    className="sui-w-auto sui-mb-3"
                    data-testid="product-pod-wrapper__kpf-wrapper"
                  >
                    <KeyProductFeatures
                      hideImage
                      storeId={storeId}
                      itemId={pod.itemId}
                      maxfeatures={3}
                      simple
                      oneColumnGrid
                      firstKPFName="Fuel Type"
                    />
                  </div>
                )}
                {!hideSelectionButton && (
                  <div>
                    <SelectionButton selectedProduct={product} />
                  </div>
                )}
              </div>
              {!openInQuickViewDescription && (
                <div className="sui-col">
                  <Image
                    alt="open quickview"
                    asset="caret-orange.svg"
                    className="sui-w-4 sui-h-4"
                    onClick={handleOnOpenQuickView}
                    role="button"
                    title="open quick view"
                  />
                </div>
              )}
            </div>
          </>
        )}
      />
      { typeof window !== 'undefined' && mountQuickView && (
        <Suspense fallback={<div />}>
          <QuickViewLazy
            isOpen={isQuickViewOpen}
            onClose={(fromClose) => {
              setIsQuickViewOpen(false);
              setIsRatingsOpen(false);
              if (fromClose) {
                onClose();
              }
            }}
            onBack={() => {
              setIsQuickViewOpen(false);
              setIsRatingsOpen(false);
            }}
            fetch={mountQuickView}
            itemId={product.itemId}
            showBackNavLink
            footerElement={hideSelectionButton ? null : <SelectionButton selectedProduct={product} />}
            isRatingsOpen={openDetailsInNewTab && isRatingsOpen}
            openDetailsInNewTab={openDetailsInNewTab}
            hideMask={hideQuickViewMask}
          />
        </Suspense>
      )}
    </div>
  );
};

ProductPodWrapper.propTypes = {
  hideKPF: boolType,
  showDimensions: boolType,
  dimensionsDisplay: shapeType({
    // [[spec names in order of preference, axis label]], e.g. FIT_DIMENSION_SPECS
    specs: arrayOfType(arrayOfType(oneOfTypeType([stringType, arrayOfType(stringType)]))),
    variant: stringType
  }),
  hideSelectionButton: boolType,
  openQuickViewOnPodClick: boolType,
  product: shapeType({}).isRequired,
  swapItemPosition: numberType,
  openInQuickViewDescription: boolType,
  openDetailsInNewTab: boolType,
  hideQuickViewMask: boolType
};

ProductPodWrapper.defaultProps = {
  hideKPF: false,
  showDimensions: false,
  dimensionsDisplay: undefined,
  hideSelectionButton: false,
  openQuickViewOnPodClick: false,
  swapItemPosition: null,
  openInQuickViewDescription: false,
  openDetailsInNewTab: false,
  hideQuickViewMask: false
};

ProductPodWrapper.displayName = 'ProductPodWrapper';

ProductPodWrapper.dataModel = extend({
  dataSources: string(),
  itemId: string(),
  identifiers: shape({
    itemId: string(),
    canonicalUrl: string(),
    productLabel: string(),
    brandName: string(),
    modelNumber: string(),
    productType: string(),
  }),
  info: shape({
    label: string(),
    productGroup: string(),
  }),
  media: shape({
    images: arrayOf({
      subType: string(),
      url: string(),
      type: string(),
      sizes: arrayOf(string())
    }),
    image: shape({
      url: string()
    }).client(),
  }),
  reviews: shape({
    ratingsReviews: shape({
      averageRating: number(),
      totalReviews: number()
    })
  }),
  pricing: params({ storeId: string() }).shape({
    value: number(),
    original: number({ float: true }),
    mapAboveOriginalPrice: bool(),
    promotionalAdjustments: shape({
      type: string(),
      description: shape({
        shortDesc: string(),
        longDesc: string()
      }),
      dollarOff: number({ float: true }),
      percentageOff: number({ float: true }),
      promoId: string(),
    }),
    mapDetail: shape({
      mapPolicy: string(),
      percentageOff: number({ float: true }),
      mapSpecialPriceViolation: bool(),
      mapOriginalPriceViolation: bool(),
      effectiveMap: number({ float: true }),
      dollarOff: number({ float: true })
    })
  })
},
KeyProductFeatures,
Price,
ProductHeader,
ProductImage,
ProductPod,
ProductRating
);
